import { PayloadAction } from '@reduxjs/toolkit'
import { isSpotSubType } from '@spiaggeit/spit-core'
import {
  FeaturedService,
  FormattedServices,
  ServiceLimitType,
  TimeSchedule,
} from '@/models/service'
import { BookingAvailability } from '@/store/bookingAvailabilitySlice'
import { getObjectsComparison } from '@/utils/getObjectsComparison'
import { normalizeReservation } from '@/utils/normalizeReservation'
import {
  getBoughtServicesTotal,
  getTimeScheduleKey,
} from '@/utils/timeSchedules'
import { CartSliceState } from '../type'

export function addFeaturedServiceToCart(
  state: CartSliceState,
  action: PayloadAction<{
    days: number[]
    featuredService: FeaturedService
    formattedServices: FormattedServices
    quantity: number
    timeSchedule: TimeSchedule
  }>
) {
  const { payload } = action
  const key = payload.featuredService.serviceId

  if (state.activeBookingType === BookingAvailability.TICKETS) {
    state.cartReservations = [
      {
        ...state.cartReservations[0],
        boughtServices: {
          ...state.cartReservations[0].boughtServices,
          [key]: {
            [getTimeScheduleKey(payload.timeSchedule)]: {
              bought: payload.quantity,
              days: payload.days,
              serviceId: key,
              serviceName: payload.featuredService.name,
              timeSchedule: payload.timeSchedule,
            },
          },
        },
      },
    ]
    return
  }

  const { formattedServices } = payload

  if (payload.featuredService.minimumLimitType !== ServiceLimitType.FLAT) {
    // add the service to the "first" reservation
    const reservationToUpdate =
      formattedServices &&
      state.cartReservations.find((res) =>
        formattedServices[key]?.sectors.includes(res.sectorId)
      )

    state.cartReservations = state.cartReservations.map((res) => {
      if (
        reservationToUpdate &&
        !getObjectsComparison(
          normalizeReservation(res),
          normalizeReservation(reservationToUpdate)
        )
      ) {
        return res
      } else
        return {
          ...res,
          boughtServices: {
            ...res.boughtServices,
            [key]: {
              [getTimeScheduleKey(payload.timeSchedule)]: {
                bought: payload.quantity,
                days: payload.days,
                serviceId: key,
                serviceName: payload.featuredService.name,
                timeSchedule: payload.timeSchedule,
              },
            },
          },
        }
    })
    return
  }

  const newCartReservations = state.cartReservations.map((res) => {
    // remove service from cart
    if (res.boughtServices[key]) {
      const newBoughtServices = { ...res.boughtServices }
      delete newBoughtServices[key]
      return {
        ...res,
        boughtServices: newBoughtServices,
      }
    }
    return res
  })

  const spotsReservations = newCartReservations.filter(
    (res) =>
      isSpotSubType(res.spotType) &&
      payload.featuredService.sectors.includes(res.sectorId)
  )

  for (let i = 0; i < payload.quantity; i++) {
    // find first resertion with minimum not yet at "limit"
    const res = spotsReservations.find((res) => {
      const quantity = getBoughtServicesTotal(res.boughtServices[key])
      return quantity < payload.featuredService.minimumQuantity
    })

    const toUpdate = res ?? spotsReservations[0]
    const newQuantity = getBoughtServicesTotal(toUpdate.boughtServices[key])
    toUpdate.boughtServices[key] = {
      [getTimeScheduleKey(payload.timeSchedule)]: {
        bought: newQuantity + 1,
        days: payload.days,
        serviceId: key,
        serviceName: payload.featuredService.name,
        timeSchedule: payload.timeSchedule,
      },
    }
  }

  state.cartReservations = newCartReservations
}
